import { creatorbase, creatorAgencyQueries } from '@round/api';
import { ModalBaseProps, Skeleton } from '@round/ui-kit';
import { Label, assets } from '@round/whitelabel-ui';
import styles from './PostPaymentRequestModal.module.css';
import { Form, FormikErrors, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { Modal } from '@round/whitelabel-ui';
import { Button } from '@round/whitelabel-ui';
import { showNotification } from 'helpers';
import { Banner } from '@round/whitelabel-ui';
import PaymentRequest from './PaymentRequest/PaymentRequest';
import {
    useCampaign,
    useCancelPaymentRequest,
    useClientInfluencer,
    useClientInfluencerAccount,
    useClientPayees,
    useClientProviderFeeConfigs,
    useCreatePaymentRequest,
    usePostPaymentRequest,
    usePreviewPostCost,
    useProject,
    useTeam,
} from '@round/creatorbase-helpers/api-hooks';
import { getClientInfluencerPlatformCost } from 'Modules/Plans/Posts/helpers';
import CreatorCostMismatchBanner from 'Modules/Plans/Posts/components/CreatorCostMismatchBanner/CreatorCostMismatchBanner';
import {
    getNormalizedPaymentCost,
    isCreatorCostMismatched,
} from 'Modules/Plans/Posts/components/CreatorCostMismatchBanner/helpers';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { mapClientPayeeToOption } from 'Modules/Plans/Finance/Payee/hooks/usePayeeSelect';
import { mapAgencyPayeeToOption } from 'Modules/Plans/Finance/Payee/hooks/useAgencyPayeeSelect';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { useCurrentPaymentDetails } from './useCurrentPaymentDetails';
import { file } from '@round/utils';
import { getPostPlatformDetails } from 'Modules/Plans/Posts/helpers/getPostPlatformDetails';
import { FormValues, Provider, validationSchema } from './helpers';
import PaymentBreakdown from './PaymentBreakdown/PaymentBreakdown';
import PaymentRequestForm from 'Modules/Plans/Finance/PaymentRequest/PaymentRequestForm/PaymentRequestForm';
import { asMoney } from 'helpers';
import { useFeatureFlags } from 'utility/featureFlags/FeatureFlagsContext';

const { TriangleExclamation: WarningIcon } = assets;

type Props = ModalBaseProps & {
    currencyId?: number | undefined;
    clientId: number | undefined;
    post: creatorbase.Post;
    updatePost?: (formValues: FormValues) => Promise<{
        success: boolean;
        errors?: FormikErrors<FormValues>;
    }>;
    onPaymentRequestUpdated?: () => void;
    defaultValues?: Partial<Pick<FormValues, 'paypalEmail' | 'cost'>>;
    areDefaultValuesLoading?: boolean;
    shouldDisablePayment?: boolean;
    // The post's group is wrapped up: its cost and currency can't change, but the payment method can
    isCostLocked?: boolean;
};

const PostPaymentRequestModal = ({
    isOpen,
    onClose,
    currencyId: campaignCurrencyId,
    clientId,
    post,
    updatePost,
    onPaymentRequestUpdated,
    defaultValues,
    areDefaultValuesLoading,
    shouldDisablePayment,
    isCostLocked,
}: Props) => {
    const { getCurrencyOptionsById, getCurrencyById } = useCurrencies();
    const isCreditLimitBannerEnabled = useFeatureFlags().isEnabled('rou_211_payment_credit_limit_banner');

    const { data: campaign } = useCampaign(post.campaign_id, {
        enabled: !!post.campaign_id && isCreditLimitBannerEnabled,
    });
    const { data: project } = useProject(campaign?.project_id, {
        enabled: !!campaign?.project_id && isCreditLimitBannerEnabled,
    });
    const { data: team } = useTeam(project?.team_id, {}, { enabled: !!project?.team_id && isCreditLimitBannerEnabled });

    const {
        data: latestPostPaymentRequest,
        isFetching: isLatestPostPaymentRequestFetching,
        error: latestPostPaymentRequestError,
        invalidate: invalidatePostPaymentRequests,
    } = usePostPaymentRequest(post.payment_request_id, { enabled: isOpen });

    // The create response carries the new payment request, but `usePostPaymentRequest` is keyed on
    // `post.payment_request_id`, which only updates after the parent refetches the post. Until then the
    // fetched request is stale (still null/FAILED/CANCELLED), which would let a second request be made for
    // the same post. Prefer the just-submitted request so the submit gate flips immediately.
    const [justSubmittedPaymentRequest, setJustSubmittedPaymentRequest] =
        useState<creatorbase.PostPaymentRequest | null>(null);
    const effectiveLatestPaymentRequest = justSubmittedPaymentRequest ?? latestPostPaymentRequest;

    useEffect(() => {
        // Drop the local copy only once the canonical fetched request catches up. Clearing it on close
        // would reopen the duplicate-submit window: the modal stays mounted, so reopening before the parent
        // refetch lands would fall back to the stale fetched request and re-enable the submit gate.
        if (latestPostPaymentRequest?.id === justSubmittedPaymentRequest?.id) {
            setJustSubmittedPaymentRequest(null);
        }
    }, [latestPostPaymentRequest?.id, justSubmittedPaymentRequest?.id]);

    const canSubmitPaymentRequest =
        !isLatestPostPaymentRequestFetching &&
        (!effectiveLatestPaymentRequest || ['FAILED', 'CANCELLED'].includes(effectiveLatestPaymentRequest.status));
    const canCancelPaymentRequest =
        !isLatestPostPaymentRequestFetching && effectiveLatestPaymentRequest?.status === 'PENDING';

    // Read synchronously inside onSuccess below, which can be called from a closure created before a
    // state update from the same submit has re-rendered this component.
    const keepModalOpenForMismatchRef = useRef(false);
    const [mismatchAfterSave, setMismatchAfterSave] = useState<{ cost: number; currencyId: number } | null>(null);

    const [{ mutateAsync: createPaymentRequest }, createPaymentRequestUtils] = useCreatePaymentRequest({
        onSuccess: (paymentRequest) => {
            // Reflect the new request locally so the submit gate closes before the parent refetch lands.
            setJustSubmittedPaymentRequest(paymentRequest);
            showNotification('Payment request submitted', 'info');
            invalidatePostPaymentRequests();
            onPaymentRequestUpdated?.();
            if (!keepModalOpenForMismatchRef.current) {
                // Close the modal so the re-enabled form can't be submitted again for the same post.
                onClose();
            }
        },
        onError: (error) => {
            const errorMessage = createPaymentRequestUtils.getErrorMessage(error) ?? 'Could not submit payment request';
            showNotification(errorMessage, 'error');
        },
    });

    const [{ mutate: cancelPaymentRequest, isPending: isCancelPaymentRequestPending }, cancelPaymentRequestUtils] =
        useCancelPaymentRequest(effectiveLatestPaymentRequest?.id);

    const handleCancelPaymentRequest = async () => {
        if (!effectiveLatestPaymentRequest?.id) {
            return;
        }

        cancelPaymentRequest(effectiveLatestPaymentRequest.id, {
            onSuccess: () => {
                showNotification('Payment request cancelled', 'info');
                invalidatePostPaymentRequests();
                invalidatePostPaymentDetails();
                onPaymentRequestUpdated?.();
            },
            onError: (error) => {
                const errorMessage =
                    cancelPaymentRequestUtils.getErrorMessage(error) ?? 'Could not cancel payment request';
                showNotification(errorMessage, 'error');
            },
        });
    };

    const { clientInfluencer: clientInfluencerAccount } = useClientInfluencerAccount(
        post.client_influencer_account_id,
        { enabled: isOpen }
    );
    const { data: clientInfluencer } = useClientInfluencer(clientInfluencerAccount?.client_influencer_id, {
        enabled: isOpen,
    });
    const creatorPlatformCost =
        post.platform === 'tiktok' || post.platform === 'instagram'
            ? getClientInfluencerPlatformCost(clientInfluencer, post.platform)
            : null;

    const handleSubmitPayment = async (values: FormValues, helpers: FormikHelpers<FormValues>) =>
        createPaymentRequest(
            {
                post_id: post.id,
            },
            {
                onSuccess: () => {
                    helpers.resetForm();
                },
            }
        );

    const { data: feeConfigs } = useClientProviderFeeConfigs({ client_id: clientId });
    const getSelectedFeeConfig = (provider: Provider | null | undefined) => {
        return feeConfigs?.results.find((feeConfig) => feeConfig.provider_name === provider)?.provider_fee_config_id;
    };

    const {
        postPaymentDetails,
        isFetching: arePostPaymentDetailsFetching,
        isLoading: isPostPaymentDetailsLoading,
        isSuccess: postPaymentDetailsIsSuccess,
        hasError: postPaymentDetailsError,
        file: initialFileUpload,
        hasFileError,
        invalidate: invalidatePostPaymentDetails,
    } = useCurrentPaymentDetails(
        { postId: post.id, postPaymentRequestId: post.payment_request_id },
        { enabled: isOpen }
    );
    const hasNoExistingPaymentDetails = !postPaymentDetails && postPaymentDetailsIsSuccess;

    const initialPayeeId = postPaymentDetails?.tipalti_payee_id ?? undefined;
    const initialAgencyPayeeId = postPaymentDetails?.creator_agency_payee_id ?? undefined;

    const {
        data: initialPayee,
        isLoading: isInitialPayeeLoading,
        isError: isInitialPayeeError,
    } = useClientPayees(
        { tipalti_payee_id: initialPayeeId, page_size: 1, client_id: clientId },
        {
            enabled: !!initialPayeeId && !!clientId,
            select: (data) => data.results[0] || null,
        }
    );

    const { data: initialAgencyPayee, isLoading: isInitialAgencyPayeeLoading } = useQuery({
        ...creatorAgencyQueries.payee.options.list({ id: initialAgencyPayeeId, page_size: 1 }),
        select: (data) => data.results[0] || null,
        enabled: !!initialAgencyPayeeId,
    });
    const initialAgencyPayeeOption = useMemo(
        () => (initialAgencyPayee ? mapAgencyPayeeToOption(initialAgencyPayee) : null),
        [initialAgencyPayee]
    );

    const { values, setFieldValue, ...formik } = useFormik<FormValues>({
        initialValues: {
            cost: (() => {
                if (hasNoExistingPaymentDetails) {
                    return post.cost || defaultValues?.cost;
                }

                return postPaymentDetails?.base_cost;
            })(),
            currency: getCurrencyOptionsById(postPaymentDetails?.currency_id || campaignCurrencyId),
            notes: postPaymentDetails?.notes ?? '',
            fileUpload: initialFileUpload || '',
            submitType: null,
            provider: postPaymentDetails?.fee_config_id
                ? feeConfigs?.results.find(
                      (feeConfig) => feeConfig.provider_fee_config_id === postPaymentDetails?.fee_config_id
                  )?.provider_name
                : null,
            paypalEmail: (postPaymentDetails?.paypal_email_address || defaultValues?.paypalEmail) ?? '',
            payee: useMemo(() => (initialPayee ? mapClientPayeeToOption(initialPayee) : null), [initialPayee]),
            agencyPayee: initialAgencyPayeeOption,
        },
        onSubmit: async (values, helpers) => {
            try {
                helpers.setSubmitting(true);

                const response = await updatePost?.({
                    ...values,
                    fileUpload: file.getHasFileChanged(
                        values.fileUpload || null,
                        formik.initialValues?.fileUpload || null
                    )
                        ? values.fileUpload
                        : undefined,
                });

                if (response?.success) {
                    // Ensures if the modal is reopened, the new cost is fetched from the API.
                    invalidatePostPaymentDetails();

                    const submittedCurrencyId = values.currency?.value ?? null;
                    const submittedCost = getNormalizedPaymentCost(values.cost);
                    const isMismatched = creatorPlatformCost
                        ? isCreatorCostMismatched(
                              creatorPlatformCost.cost,
                              creatorPlatformCost.currencyId,
                              submittedCost,
                              submittedCurrencyId
                          )
                        : false;

                    // Only known once the save/payment actually goes through - checking while the user is
                    // still typing would flag values they haven't committed to yet.
                    keepModalOpenForMismatchRef.current = isMismatched;
                    setMismatchAfterSave(
                        isMismatched && submittedCost !== null && submittedCurrencyId
                            ? { cost: submittedCost, currencyId: submittedCurrencyId }
                            : null
                    );

                    if (values.submitType !== 'submit') {
                        showNotification('Cost updated', 'info');
                        if (!isMismatched) {
                            onClose();
                        }
                        return;
                    }

                    await handleSubmitPayment(values, helpers);
                    return;
                }

                if (response?.errors) {
                    helpers.setErrors(response.errors);
                    return;
                }

                showNotification('Could not update cost', 'error');
            } catch {
                showNotification('Could not update cost', 'error');
            } finally {
                helpers.setSubmitting(false);
                helpers.setFieldValue('submitType', null);
            }
        },
        validationSchema: validationSchema,
        enableReinitialize: true,
    });

    const campaignCurrency = getCurrencyOptionsById(campaignCurrencyId);
    const selectedCurrency = values.currency || campaignCurrency;

    const hasCostChanged =
        formik.dirty && isFinite(Number(values.cost)) && Number(values.cost) !== Number(formik.initialValues?.cost);
    const hasCurrencyChanged = values.currency !== formik.initialValues?.currency;
    const hasFeeConfigChanged = values.provider !== formik.initialValues?.provider;
    const hasPayeeChanged = values.payee?.value !== formik.initialValues?.payee?.value;
    const hasAgencyPayeeChanged = values.agencyPayee?.value !== formik.initialValues?.agencyPayee?.value;
    //TODO: extract to util
    const hasFileUploadChanged = file.getHasFileChanged(
        values.fileUpload || null,
        formik.initialValues?.fileUpload || null
    );
    const hasChangedValues =
        hasCostChanged ||
        hasCurrencyChanged ||
        hasFeeConfigChanged ||
        hasPayeeChanged ||
        hasAgencyPayeeChanged ||
        hasFileUploadChanged;

    const {
        data: costPreview,
        isLoading: isPreviewLoading,
        isFetching: isPreviewFetching,
    } = usePreviewPostCost(
        {
            campaign_id: post.campaign_id,
            base_cost: values.cost ?? undefined,
            currency_id: selectedCurrency?.value,
            fee_config_id: getSelectedFeeConfig(values.provider),
            client_payee_id: values.provider === 'TIPALTI' ? values.payee?.value : undefined,
            client_id: clientId,
            paypal_email_address: values.provider === 'PAYPAL' ? values.paypalEmail || undefined : undefined,
            creator_agency_payee_id: values.provider === 'CREATOR_AGENCY' ? values.agencyPayee?.value : undefined,
        },
        {
            enabled:
                !!values.cost &&
                isOpen &&
                (hasNoExistingPaymentDetails ||
                    postPaymentDetails?.payment_request_status === 'FAILED' ||
                    hasChangedValues),
        }
    );

    // If there's no existing post cost to have taken the initial fee config value from,
    // use the one from the initial preview as it will be default used on the BE.
    const defaultProvider =
        hasNoExistingPaymentDetails && !values.provider
            ? feeConfigs?.results.find((feeConfig) => costPreview?.fee_config_id === feeConfig.provider_fee_config_id)
                  ?.provider_name
            : null;

    const selectedProvider = values.provider || defaultProvider;

    const payeeOrdering = {
        [`order_by_${post.platform}_account_id`]: getPostPlatformDetails(post, post.platform)?.account_id ?? undefined,
    };

    const costsToUse =
        hasNoExistingPaymentDetails || postPaymentDetails?.payment_request_status === 'FAILED' || hasChangedValues
            ? costPreview
            : postPaymentDetails;

    const isBreakdownFetching = isPreviewFetching || arePostPaymentDetailsFetching;
    const isBreakdownLoading = isPreviewLoading || isPostPaymentDetailsLoading;

    const creditCurrencyCost = (() => {
        if (!costsToUse || !team || costsToUse.currency_id !== team.payment_credit_currency_id) {
            return null;
        }

        return costsToUse.cost;
    })();

    const wouldExceedCredit =
        team?.max_payment_credit != null &&
        creditCurrencyCost != null &&
        team.current_payment_credit + creditCurrencyCost > team.max_payment_credit;

    const creditCurrency = getCurrencyById(team?.payment_credit_currency_id);
    const amountOverCredit =
        wouldExceedCredit && team && creditCurrencyCost != null
            ? team.current_payment_credit + creditCurrencyCost - (team.max_payment_credit ?? 0)
            : null;

    const handleClose = () => {
        if (formik.isSubmitting) {
            return;
        }

        onClose();
        formik.resetForm();
        keepModalOpenForMismatchRef.current = false;
        setMismatchAfterSave(null);
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} className={styles.modal} closeOnOverlayClick closeOnEscape>
            <Modal.Header>
                <Modal.Title>Request Payment</Modal.Title>
            </Modal.Header>
            <Modal.Body className={styles.container}>
                {isLatestPostPaymentRequestFetching ? (
                    <Skeleton height="4rem" />
                ) : latestPostPaymentRequestError ? (
                    <Banner className={styles.banner} appearance="destructive">
                        <WarningIcon className={styles.errorIcon} />
                        <p>Something went wrong fetching payment request data.</p>
                    </Banner>
                ) : effectiveLatestPaymentRequest ? (
                    <>
                        <div>
                            <Label className={styles.sectionTitle}>Payment</Label>
                            <PaymentRequest data={effectiveLatestPaymentRequest} />
                        </div>
                        {canSubmitPaymentRequest && <hr className={styles.separator} />}
                    </>
                ) : null}

                {postPaymentDetailsError && (
                    <Banner className={styles.banner} appearance="destructive">
                        <WarningIcon className={styles.errorIcon} />
                        <p>Something went wrong fetching payment details data.</p>
                    </Banner>
                )}
                <FormikProvider value={{ ...formik, values, setFieldValue }}>
                    <Form className={styles.formContainer}>
                        <PaymentRequestForm
                            clientId={clientId}
                            canSubmitPaymentRequest={canSubmitPaymentRequest}
                            defaultProvider={defaultProvider}
                            isLoading={
                                isPostPaymentDetailsLoading ||
                                isInitialPayeeLoading ||
                                isInitialAgencyPayeeLoading ||
                                areDefaultValuesLoading
                            }
                            isDisabled={isLatestPostPaymentRequestFetching || arePostPaymentDetailsFetching}
                            isCostDisabled={isCostLocked}
                            payeeOrdering={payeeOrdering}
                            expectedPaypalEmail={defaultValues?.paypalEmail}
                            isInitialPayeeError={isInitialPayeeError}
                            hasFileError={!!hasFileError}
                        />

                        {mismatchAfterSave &&
                            creatorPlatformCost &&
                            (post.platform === 'tiktok' || post.platform === 'instagram') && (
                                <CreatorCostMismatchBanner
                                    clientInfluencerId={clientInfluencer?.id}
                                    platform={post.platform}
                                    creatorCost={creatorPlatformCost.cost}
                                    creatorCurrencyId={creatorPlatformCost.currencyId}
                                    paymentCost={mismatchAfterSave.cost}
                                    paymentCurrencyId={mismatchAfterSave.currencyId}
                                    className={styles.banner}
                                />
                            )}

                        <div className={styles.breakdown}>
                            <PaymentBreakdown
                                isLoading={isBreakdownLoading}
                                isFetching={isBreakdownFetching}
                                costs={costsToUse}
                                selectedCurrencyId={selectedCurrency?.value}
                                campaignCurrencyId={campaignCurrencyId}
                                provider={selectedProvider}
                            />

                            {selectedProvider === 'TIPALTI' && !values.payee && (
                                <Banner className={styles.banner} appearance="warning">
                                    <WarningIcon className={styles.warningIcon} />
                                    <p>Please select a payee to see the full estimate</p>
                                </Banner>
                            )}

                            {selectedProvider === 'CREATOR_AGENCY' && !values.agencyPayee && (
                                <Banner className={styles.banner} appearance="warning">
                                    <WarningIcon className={styles.warningIcon} />
                                    <p>Please select an agency to see the full estimate</p>
                                </Banner>
                            )}

                            {isCreditLimitBannerEnabled && wouldExceedCredit && team && (
                                <Banner className={styles.banner} appearance="destructive">
                                    <WarningIcon className={styles.errorIcon} />
                                    <p>
                                        This payment would exceed the team's credit limit by{' '}
                                        <strong>{asMoney(amountOverCredit, creditCurrency)}</strong>. Current credit
                                        used: {asMoney(team.current_payment_credit, creditCurrency)} of{' '}
                                        {asMoney(team.max_payment_credit, creditCurrency)}.
                                    </p>
                                </Banner>
                            )}
                        </div>
                    </Form>
                </FormikProvider>
            </Modal.Body>
            <Modal.Actions className={styles.actions}>
                <Button
                    appearance="ghost"
                    type="button"
                    onClick={() => {
                        setFieldValue('submitType', 'save');
                        formik.submitForm();
                    }}
                    isLoading={formik.isSubmitting && values.submitType !== 'submit'}
                    disabled={
                        (!hasChangedValues && !formik.dirty && !hasNoExistingPaymentDetails) ||
                        !formik.isValid ||
                        formik.isSubmitting ||
                        isBreakdownLoading ||
                        isCancelPaymentRequestPending ||
                        !canSubmitPaymentRequest
                    }
                >
                    Save Changes
                </Button>

                {shouldDisablePayment ? null : canCancelPaymentRequest ? (
                    <Button
                        appearance="critical"
                        type="button"
                        onClick={handleCancelPaymentRequest}
                        isLoading={isCancelPaymentRequestPending}
                        disabled={isCancelPaymentRequestPending || shouldDisablePayment}
                    >
                        Cancel Payment
                    </Button>
                ) : (
                    <Button
                        appearance="primary"
                        type="button"
                        onClick={async () => {
                            setFieldValue('submitType', 'submit');
                            const errors = await formik.validateForm();
                            if (Object.keys(errors).length > 0) {
                                return;
                            }
                            formik.submitForm();
                        }}
                        isLoading={formik.isSubmitting && values.submitType === 'submit'}
                        disabled={
                            !canSubmitPaymentRequest ||
                            !formik.isValid ||
                            formik.isSubmitting ||
                            isBreakdownLoading ||
                            (isCreditLimitBannerEnabled && wouldExceedCredit)
                        }
                    >
                        {hasChangedValues || formik.dirty ? 'Save and Make Payment' : 'Make Payment'}
                    </Button>
                )}
            </Modal.Actions>
        </Modal>
    );
};

export default PostPaymentRequestModal;
